/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input, OnChanges,
  Output, SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {TypeaheadHelperService} from '../../../shared/services/typeahead-helper.service';
import {objectsValidator} from '../../../shared/input/input-validators';
import {
    ValidatorFn,
    Validators
} from '@angular/forms';
import {WarningsEnum} from '../../../shared/warnings/warnings.enum';
import {
    Observable,
    of,
    Subject,
    Subscription
} from 'rxjs';
import {StatefulComponent} from '../../../shared/stateful/stateful.component';
import {MaterialApi} from '../../../api/ms-partnumberchanges/material.api';
import {MaterialDetailContract} from '../../../api/ms-partnumberchanges/contracts/material-detail/model/material-detail.contract';
import {InputAutocompleteComponent} from '../../../shared/input/input-autocomplete/input-autocomplete.component';
import {InputTextComponent} from '../../../shared/input/input-text/input-text.component';
import {PartNumberChangeSolutionMaterialDetailModel} from '../../models/part-number-change-solution-material-detail.model';
import {Clipboard} from '@angular/cdk/clipboard';
import {ToastWrapperService} from '../../../shared/utils/toast-wrapper-service/toast-wrapper-service';

@Component({
    selector: 'app-part-number-changes-detail-pnc-solution-new-part-number',
    templateUrl: './part-number-changes-new-part-number.component.html',
    styleUrls: ['../../styles/pnc-base.component.css', './part-number-changes-new-part-number.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PartNumberChangesNewPartNumberComponent
        extends StatefulComponent<PartNumberChangeSolutionMaterialDetailModel>
        implements AfterViewInit, OnChanges {
    @Input() isInViewMode: boolean;
    @Input() isDetailSolvedTrial: boolean;
    @Input() supplyingPackingPlantCodeChange: Subject<string>;
    @Input() additionalIndex?: number;
    @Input() supplyingPackingPlantCode: string;
    @Input() material: PartNumberChangeSolutionMaterialDetailModel;
    @Input() materialWarningLevel: WarningsEnum;
    @Input() quantityWarningLevel: WarningsEnum;
    @Input() isSolutionSpecialist: boolean;
    @Output() deleteClick = new EventEmitter<void>();
    @Output() newPartChange = new EventEmitter<PartNumberChangeSolutionMaterialDetailModel>();
    @ViewChild('materialInput') materialInput: InputAutocompleteComponent;
    @ViewChild('quantityInput') quantityInput: InputTextComponent;
    validators: { [key: string]: ValidatorFn[] } = {
        material: [objectsValidator],
        quantity: [Validators.maxLength(6), Validators.pattern(/^[1-9]{1}[0-9]{0,5}$/)]
    };
    WarningsEnum = WarningsEnum;
    materialMessages = new Map<WarningsEnum, string[]>();
    quantityMessages = new Map<WarningsEnum, string[]>();
    public readonly CYPRESS_TAG = 'pnc-additional-part-number';
    public readonly STYLE_TAG = 'pnc-additional-part-number';
    public readonly STYLE_TAG_EE_SOLUTION = 'pnc-additional-part-number-solution';
    public readonly LABEL_NEW = 'New part number';
    public readonly LABEL_ADDITIONAL = 'Additional part number';
    private subscriptions = new Subscription();

    get materialLabel(): string {
        return this.additionalIndex != null ? `${this.LABEL_ADDITIONAL} ${this.additionalIndex}` : this.LABEL_NEW;
    }

    get baseCypressTag(): string {
        return this.additionalIndex != null ? `${this.CYPRESS_TAG}-${this.additionalIndex}` : this.CYPRESS_TAG;
    };

    get showViewMode(): boolean {
        return this.isInViewMode || this.isDetailSolvedTrial;
    };

    get invertedColorElements(): boolean {
        return this.isInViewMode && !this.isDetailSolvedTrial && !this.isSolutionSpecialist;
    };

    get styleTag(): string{
        return this.isSolutionSpecialist ? this.STYLE_TAG_EE_SOLUTION : this.STYLE_TAG;
    };

    constructor(public typeahead: TypeaheadHelperService,
                private materialApi: MaterialApi,
                private clipboard: Clipboard,
                private toast: ToastWrapperService,
                private changeDetector: ChangeDetectorRef) {
        super();
    }

    ngAfterViewInit(): void {
        this.storeState(this.material);
        if (this.supplyingPackingPlantCodeChange != null) {
            if (this.supplyingPackingPlantCode == null) {
                this.toggleInputs(true);
            }
            this.setSupplyingPackingPlantChangeSubscription();
            this.changeDetector.detectChanges();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        const change = changes['supplyingPackingPlantCode'];
        if (change && change.currentValue !== change.previousValue) {
            this.manageMaterialInputAfterPlantChange(changes['supplyingPackingPlantCode']?.currentValue);
        }
    }

    delete(): void {
        this.deleteClick.emit();
    }

    searchMaterials = (term: string): Observable<MaterialDetailContract[]> => {
        if (term != null && term.length > 0) {
            return this.materialApi.getAllActiveMaterials(term, this.supplyingPackingPlantCode, 0, 20);
        }
        return of([]);
    };

    storeState(state?: Partial<PartNumberChangeSolutionMaterialDetailModel>): void {
        super.storeState(state);
        super.storeState({
            isValid: this.isInViewMode || (this.quantityIsValid() && this.materialIsValid()),
            id: this.material?.id
        });
        this.newPartChange.emit(this.state);
    }

    quantityIsValid(): boolean {
        let isValid = this.quantityInput.isValid;
        if (this.inputIsRequired(this.quantityWarningLevel)) {
            isValid = this.state.quantity?.length > 0 && isValid;
        }
        return isValid;
    }

    materialIsValid(): boolean {
        let isValid = this.materialInput.isValid;
        if (this.inputIsRequired(this.materialWarningLevel)) {
            isValid = this.state.material?.constructor === Object.prototype.constructor && isValid;
        }
        return isValid;
    }

    materialChange(event: MaterialDetailContract | string): void {
        this.storeState({
            material: event
        });

        this.manageMaterialMessages();
    }

    quantityChange(event: string): void {
        this.storeState({
            quantity: event
        });

        this.manageQuantityMessages();
    }

    getInitialValue(): string {
        if (this.material?.quantity === undefined) {
            this.quantityChange(null);
        } else {
            this.quantityInput.initialValue = this.material?.quantity;
        }
        return this.material?.quantity;
    }

    copyMaterialNumber(): void {
        let copyValue = null;

        if (this.material?.constructor === Object.prototype.constructor) {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            const material = <MaterialDetailContract> this.material.material;
            copyValue = material?.number ?? null;

            if (copyValue != null) {
                this.clipboard.copy(copyValue);
                this.toast.success(`The part number ${copyValue} was successfully copied to clipboard`, 'Success');
            }
        }
    }

    initialValuePipe(cellValue: any): string {
        let value = '-';

        if (cellValue != null || !this.isInViewMode) {
            value = cellValue;
        }

        return value;
    }

    private toggleInputs(disabled: boolean): void {
        if (disabled) {
            this.materialInput?.disable();
            this.quantityInput?.disable();
        } else {
            this.materialInput?.enable();
            this.quantityInput?.enable();
        }

        this.materialMessages.delete(WarningsEnum.WARNING);
        this.quantityMessages.delete(WarningsEnum.WARNING);
    }

    /**
     * Validates a material from a specific supplying packing plant
     * @param materialNumber The material number to be validated
     * @param plantCode The plant code for which to check if the material is valid
     */
    private validateMaterial(materialNumber: string, plantCode: string): void {
        this.subscriptions.add(
                this.materialApi.getAllActiveMaterials(materialNumber, plantCode, 0, 20)
                        .subscribe((validation) => {
                            if (validation.length === 0) {
                                this.materialInput.markAsInvalid();
                                this.materialMessages.set(WarningsEnum.ERROR,
                                        ['This material is not available in the selected packing plant']);
                                this.materialMessages = new Map(this.materialMessages);
                                this.storeState({isValid: false});
                                this.newPartChange.emit(this.state);
                            }
                        }));
    }

    private inputIsRequired(warningLevel: WarningsEnum): boolean {
        return warningLevel === WarningsEnum.WARNING_PNC_SEND;
    }

    private manageMaterialMessages(): void {
        const messages = new Map<WarningsEnum, string[]>();

        if (this.materialInput?.inputControl?.value == null || this.materialInput?.inputControl?.value.length === 0) {
            this.addInputMessage(messages, WarningsEnum.WARNING, 'Mandatory field to send');
        } else if (this.materialInput?.inputControl?.invalid === true) {
            this.addInputMessage(messages, WarningsEnum.ERROR, 'Please enter a valid material');
        }

        this.materialMessages = messages;
    }

    private manageQuantityMessages(): void {
        const messages = new Map<WarningsEnum, string[]>();

        if ((this.quantityInput?.inputControl?.value == null || this.quantityInput?.inputControl?.value.length === 0)
                && this.inputIsRequired(this.quantityWarningLevel)) {
            this.addInputMessage(messages, WarningsEnum.WARNING, 'Mandatory field to send');
        } else if (this.quantityInput?.inputControl?.invalid === true) {
            this.addInputMessage(messages, WarningsEnum.ERROR, 'Please enter a valid quantity, e.g. 1');
        }

        this.quantityMessages = messages;
    }

    private addInputMessage = (map: Map<WarningsEnum, string[]>, level: WarningsEnum, message: string): void => {
        if (map != null && level != null && message != null) {
            if (map.has(level)) {
                map.get(level)
                        .push(message);
            } else {
                map.set(level, [message]);
            }
        }
    };

    private setSupplyingPackingPlantChangeSubscription(): void {
        this.subscriptions.add(
                this.supplyingPackingPlantCodeChange
                        .subscribe((supplyingPackingPlantCode: string) => {
                            this.supplyingPackingPlantCode = supplyingPackingPlantCode;
                            this.toggleInputs(supplyingPackingPlantCode == null);
                            this.manageMaterialInputAfterPlantChange(supplyingPackingPlantCode);
                        })
        );
    }

    private manageMaterialInputAfterPlantChange(plant: string): void {
        let material = null;
        if (this.state.material?.constructor === Object.prototype.constructor) {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            material = <MaterialDetailContract> this.state.material;
            if (plant != null) {
                if (material?.number != null) {
                    this.validateMaterial(material.number, this.supplyingPackingPlantCode);
                }
            }
        }
    }
}
